/**
 * Roles — Settings › User & Access › Roles & Permissions.
 *
 * Two different needs, two different reads:
 *  - `hydrate()`/`myPermissions()` — the CALLER's own role, fetched via
 *    `GET /roles/mine` (no special permission needed, same as
 *    `groupService.mine()`). Held in memory so `can()` in auth/permissions.ts
 *    can resolve synchronously on every render, the same pattern
 *    `moduleService`'s cache already uses for modules.
 *  - `list()`/`create()`/`update()`/`remove()` — the full roster, for the
 *    Roles & Permissions admin screen. Needs `roles:manage`; a user without
 *    it never calls these.
 *
 * Admin-only rules (create/rename/delete on a NON-system role only — `admin`
 * and `user` are fixed) are enforced server-side; this file adds no gate of
 * its own, matching `groupService`.
 */

import type { RoleDef } from '../types';
import { api, ApiError } from './apiClient';

interface MyRole {
  id: string;
  name: string;
  permissions: string[];
}

function describeFailure(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    if (error.unauthenticated) return 'Your session has expired. Sign in again.';
    if (error.status === 403) return 'You do not have permission to manage roles.';
    if (error.status === 404) return 'This role no longer exists — it may have just been deleted.';
    if (error.status === 0) return 'Could not reach the server. Check your connection and try again.';
    return error.message;
  }
  return fallback;
}

/** The caller's own role, held in memory — see this file's own doc comment. */
let myRole: MyRole | null = null;
let hydrated = false;

export const roleService = {
  /** Loads the caller's own role. Call once after sign-in, alongside
   *  `moduleService.hydrate()`. */
  async hydrate(): Promise<MyRole> {
    const role = await api.get<MyRole>('/roles/mine');
    myRole = role;
    hydrated = true;
    return role;
  },

  /** Drops everything held for the previous session. Call on sign-out. */
  reset(): void {
    myRole = null;
    hydrated = false;
  },

  /** True once `hydrate` has completed; false before the first load. */
  isReady(): boolean {
    return hydrated;
  },

  /** The caller's own role id's permission set — what `can()` reads.
   *  Empty (never `null`) before hydration, so a check made too early fails
   *  closed rather than throwing. */
  myPermissions(): string[] {
    return myRole?.permissions ?? [];
  },

  myRoleName(): string | null {
    return myRole?.name ?? null;
  },

  /** The full roster — admin console only, needs `roles:manage`. */
  async list(): Promise<RoleDef[]> {
    try {
      return await api.get<RoleDef[]>('/roles');
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not load roles.'));
    }
  },

  async create(input: { name: string; description: string; permissions: string[] }): Promise<RoleDef> {
    try {
      return await api.post<RoleDef>('/roles', input);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not create this role.'));
    }
  },

  async update(
    id: string,
    input: { name?: string; description?: string; permissions?: string[] },
  ): Promise<RoleDef> {
    try {
      return await api.put<RoleDef>(`/roles/${id}`, input);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not update this role.'));
    }
  },

  async remove(id: string): Promise<void> {
    try {
      await api.delete<void>(`/roles/${id}`);
    } catch (error) {
      throw new Error(describeFailure(error, 'Could not delete this role.'));
    }
  },
};
